<template>
  <div class="access-requests">
    <div class="access-requests__intro">
      <h3 class="access-requests__title">Access requests</h3>
      <p class="access-requests__help">
        New people never join the company on their own. When someone signs up with your company's email domain
        (and verifies it), opens a manager's invite link, or a manager requests a new user, the tenant owner or an
        admin approves or declines it here. Approving adds them to the company (and to the requesting manager's
        team); it never gives admin or financial access. A manager's request may show an email that is not verified
        yet: the person verifies it when they first sign in.
      </p>
    </div>

    <div v-if="actionError" class="access-requests__message access-requests__message--error">
      <i class="mdi mdi-alert-circle"></i> {{ actionError }}
    </div>
    <div v-if="actionNotice" class="access-requests__message access-requests__message--notice">
      <i class="mdi mdi-check-circle-outline"></i> {{ actionNotice }}
    </div>

    <div v-if="isLoading" class="tw-state">
      <b-loading :is-full-page="false" :active="true" />
      <p>Loading access requests...</p>
    </div>

    <div v-else-if="loadError" class="tw-state tw-state--error">
      <p>{{ loadError }}</p>
    </div>

    <b-table
      v-else
      :data="requests"
      :striped="true"
      :hoverable="true"
    >
      <template #empty>
        <div class="empty-msg">No open access requests.</div>
      </template>

      <b-table-column field="person" label="Person" v-slot="props">
        {{ formatName(props.row.person) }}
      </b-table-column>

      <b-table-column field="email" label="Email" v-slot="props">
        {{ props.row.person?.email || '—' }}
        <span v-if="props.row.person && props.row.person.emailVerified" class="access-requests__tag access-requests__tag--ok">Verified</span>
        <span v-else class="access-requests__tag access-requests__tag--warn">Not yet verified</span>
      </b-table-column>

      <b-table-column field="source" label="How" v-slot="props">
        {{ sourceLabel(props.row) }}
        <span v-if="props.row.sponsor && props.row.sponsorActive === false" class="access-requests__tag access-requests__tag--warn">
          Manager left the company
        </span>
      </b-table-column>

      <b-table-column field="requestDate" label="Requested" v-slot="props">
        {{ formatDate(props.row.requestDate) }}
      </b-table-column>

      <b-table-column field="actions" label="" v-slot="props">
        <div class="access-requests__actions">
          <b-button
            type="is-primary"
            size="is-small"
            :loading="busyId === props.row.id"
            :disabled="Boolean(busyId)"
            @click="decide(props.row, 'approve')"
          >
            Approve
          </b-button>
          <b-button
            type="is-text"
            size="is-small"
            :disabled="Boolean(busyId)"
            @click="decide(props.row, 'decline')"
          >
            Decline
          </b-button>
        </div>
      </b-table-column>
    </b-table>

    <div v-if="!isLoading && !loadError && nextCursor" class="access-requests__more">
      <b-button size="is-small" :loading="isLoadingMore" @click="loadMore">Load more</b-button>
    </div>
  </div>
</template>

<script>
// spec-admin-gated-company-access: the company's open access requests, for its tenant owner,
// admins and super users. The server re-checks who may decide on every call.
export default {
  name: 'AccessRequests',
  props: {
    config: {
      type: Object,
      required: true,
    },
    // Honored by the backend for super users only; everyone else gets their own company.
    companyId: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      requests: [],
      nextCursor: null,
      isLoading: false,
      isLoadingMore: false,
      loadError: null,
      actionError: null,
      actionNotice: null,
      busyId: null,
    }
  },
  watch: {
    // A super user switching companies on the Company page.
    companyId() {
      this.requests = []
      this.nextCursor = null
      this.fetchRequests()
    },
  },
  mounted() {
    this.fetchRequests()
  },
  methods: {
    async getPage(cursor) {
      const params = { ...(this.companyId ? { companyId: this.companyId } : {}), ...(cursor ? { cursor } : {}) }
      const response = await this.axios.get(`${process.env.VUE_APP_API_URL}/users/access-requests`, { ...this.config, params })
      return {
        requests: Array.isArray(response?.data?.requests) ? response.data.requests : [],
        nextCursor: typeof response?.data?.nextCursor === 'string' ? response.data.nextCursor : null,
      }
    },
    async fetchRequests() {
      this.isLoading = true
      this.loadError = null
      try {
        const page = await this.getPage(null)
        this.requests = page.requests
        this.nextCursor = page.nextCursor
      } catch (error) {
        this.loadError = error?.response?.data?.message || 'Unable to load access requests.'
      } finally {
        this.isLoading = false
      }
    },
    async loadMore() {
      if (!this.nextCursor) return
      this.isLoadingMore = true
      try {
        const page = await this.getPage(this.nextCursor)
        const seen = new Set(this.requests.map(r => r.id))
        this.requests = [...this.requests, ...page.requests.filter(r => !seen.has(r.id))]
        this.nextCursor = page.nextCursor
      } catch (error) {
        this.actionError = error?.response?.data?.message || 'Unable to load more access requests.'
      } finally {
        this.isLoadingMore = false
      }
    },
    sourceLabel(row) {
      if (row.source === 'manager_request') return `Requested by ${this.formatName(row.sponsor)} (joins their team)`
      if (row.source === 'invite_link') return `${this.formatName(row.sponsor)}'s invite link (joins their team)`
      return 'Signed up with the company email'
    },
    confirmDialog({ title, message, confirmText, danger = false }) {
      return new Promise(resolve => {
        this.$buefy.dialog.confirm({
          title,
          message,
          confirmText,
          cancelText: 'Cancel',
          ...(danger ? { type: 'is-danger' } : {}),
          onConfirm: () => resolve(true),
          onCancel: () => resolve(false),
        })
      })
    },
    confirmDecline(row) {
      return this.confirmDialog({
        title: 'Decline access',
        message: `Decline ${this.formatName(row.person)}'s request to join the company? They will be told.`,
        confirmText: 'Decline',
        danger: true,
      })
    },
    // A decided (or no longer open) request leaves the list without refetching page 1.
    removeRow(row) {
      this.requests = this.requests.filter(r => r.id !== row.id)
    },
    confirmApprove(row) {
      const verified = row.person?.emailVerified ? '' : ' Their email is not verified yet.'
      const team = row.sponsor && row.sponsorActive !== false ? ` and ${this.formatName(row.sponsor)}'s team` : ''
      return new Promise(resolve => {
        this.$buefy.dialog.confirm({
          title: 'Approve access',
          message: `Add ${this.formatName(row.person)} to the company${team}? This does not give admin or financial access.${verified}`,
          confirmText: 'Approve',
          cancelText: 'Cancel',
          onConfirm: () => resolve(true),
          onCancel: () => resolve(false),
        })
      })
    },
    formatName(person) {
      const name = `${person?.firstName || ''} ${person?.lastName || ''}`.trim()
      return name || person?.email || '—'
    },
    formatDate(value) {
      if (!value) return '—'
      const date = new Date(value)
      return Number.isNaN(date.getTime()) ? '—' : date.toLocaleDateString()
    },
    errorMessage(error) {
      const code = error?.response?.data?.code
      if (code === 'superseded') return 'This person already joined a company another way, so the request was closed.'
      if (code === 'not_found' || error?.response?.status === 404) return 'This request is no longer open.'
      if (error?.response?.status === 403) return 'Only the tenant owner or a company admin can decide this request.'
      if (code === 'retry') return 'Someone else changed this at the same time. Please try again.'
      return error?.response?.data?.message || 'Unable to update the request. Please try again.'
    },
    async decide(row, action) {
      if (action === 'approve' && !(await this.confirmApprove(row))) return
      if (action === 'decline' && !(await this.confirmDecline(row))) return
      this.actionError = null
      this.actionNotice = null
      this.busyId = row.id
      try {
        const response = await this.axios.post(
          `${process.env.VUE_APP_API_URL}/users/access-requests/${encodeURIComponent(row.id)}/${action}`,
          {},
          this.config,
        )
        const name = this.formatName(row.person)
        if (action === 'approve') {
          this.actionNotice = response?.data?.joinedTeam
            ? `${name} was added to the company and ${this.formatName(row.sponsor)}'s team.`
            : `${name} was added to the company.`
        } else {
          this.actionNotice = `${name}'s request was declined.`
        }
        this.removeRow(row)
      } catch (error) {
        this.actionError = this.errorMessage(error)
        const status = error?.response?.status
        // Closed meanwhile (404 / superseded): drop the stale row; anything else stays to retry.
        if (status === 404 || error?.response?.data?.code === 'superseded') this.removeRow(row)
      } finally {
        this.busyId = null
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.access-requests {
  display: flex;
  flex-direction: column;
  gap: $space-4;
}

.access-requests__title {
  font-size: $font-size-lg;
  font-weight: 700;
  color: $color-text-strong;
  margin: 0 0 $space-2;
}

.access-requests__help {
  color: $color-text-muted;
  font-size: $font-size-sm;
  line-height: 1.5;
  margin: 0;
}

.access-requests__tag {
  display: inline-block;
  margin-left: $space-2;
  padding: 0 $space-2;
  border-radius: $radius-pill;
  font-size: $font-size-xs;
  white-space: nowrap;
}

.access-requests__tag--ok {
  background: $color-success-soft;
  color: $color-success-strong;
}

.access-requests__tag--warn {
  background: rgba($color-warning, 0.15);
  color: $color-text-strong;
}

.access-requests__more {
  display: flex;
  justify-content: center;
}

.access-requests__actions {
  display: flex;
  gap: $space-2;
  justify-content: flex-end;
}

.access-requests__message {
  border-radius: $radius-md;
  padding: $space-3 $space-4;
  font-size: $font-size-sm;
}

.access-requests__message--notice {
  background: $color-primary-soft;
  color: $color-primary-dark;
  border: 1px solid rgba($color-primary, 0.3);
}

.access-requests__message--error {
  background: rgba($color-danger, 0.08);
  color: $color-danger;
  border: 1px solid rgba($color-danger, 0.3);
}
</style>
